//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
// onboarding-autopilot-event imports
import { IProps, IContainer, IState } from "./OnboardingAutopilotEvent.d";
import * as s from "./OnboardingAutopilotEvent.styles";
import { fragments } from "./OnboardingAutopilotEvent.gql";
// constants
import { DayOffsetTimeSlot } from "@clockwise/client-commons/src/util/time-slot";
// other components
import { AutopilotFlexibility } from "@components/autopilot-flexibility";
import {
  EventSummaryAttendees,
  EventSummaryExternalAttendees,
  EventSummaryRecurrence,
  EventSummaryTime,
  EventSummaryRoom,
} from "@components/event-summary";
import { A } from "@ui";
import { TooltipToggle } from "@clockwise/web-commons/src/ui/tooltip-toggle";
// style
import { colors, greys, helpers } from "@clockwise/web-commons/src/styles/color.styles";
// state
import { IReduxState } from "@reducers/root.reducer";
import { setSettingsDialogOpen } from "@actions/chrome-extension.actions";
import { toggleExpanded } from "@actions/settings.actions";
import { setPricingDialogOpen } from "@actions/payments.actions";
// util
import { getOrganizerFeatureStatesFromEvent } from "@util/feature-grid.util";
import { getDomainFromCalendarId } from "@util/calendar-id.util";
import { track, TrackingEvents } from "@util/analytics.util";
import { EFlex } from "@util/event-tag.util";
import { ZonedMoment } from "@util/time-zone.util";
import { eventHasRoom } from "@clockwise/client-commons/src/util/event";
import { removeAutopilotEmoji } from "@util/event.util";
import { maybeGetWarningType, getWarningText } from "@clockwise/client-commons/src/util/event-tag";
// material-ui
import { Settings, ExpandLess } from "@material-ui/icons";
import { TranslationContext } from "@clockwise/client-commons/src/providers/TranslationContext";

///////////////////
// COMPONENT
////////////////////
export class OnboardingAutopilotEventCmpt extends React.Component<IProps, IState> {
  public static defaultProps = {
    enableTracking: true,
  };

  static contextType = TranslationContext;
  declare context: React.ContextType<typeof TranslationContext>;

  constructor(props: IProps) {
    super(props);

    this.state = {
      expanded: false,
    };
  }

  private onClickInvite = () => {
    const { event, enableTracking } = this.props;
    if (enableTracking) {
      track(TrackingEvents.ONBOARDING.ONBOARDING_AUTOPILOT_EVENT_INVITE_CLICKED, {
        externalEventId: event.eventKey.externalEventId,
      });
    }
    this.props.setSettingsDialogOpen(true);
    this.props.toggleExpanded("expandInvites");
  };

  private onAutopilotToggle = () => {
    const { autopilotEnabled, event, enableTracking } = this.props;
    const { externalEventId } = event.eventKey;
    if (enableTracking) {
      track(TrackingEvents.ONBOARDING.ONBOARDING_AUTOPILOT_EVENT_TOGGLED, {
        externalEventId: event.eventKey.externalEventId,
        enabled: !autopilotEnabled,
      });
    }
    if (this.props.onAutopilotToggle) {
      this.props.onAutopilotToggle(!autopilotEnabled, externalEventId);
    }
  };

  private onChangeFlex = (selectedFlex: EFlex) => {
    const { event, enableTracking } = this.props;
    const { externalEventId } = event.eventKey;
    if (enableTracking) {
      track(TrackingEvents.ONBOARDING.ONBOARDING_AUTOPILOT_SET_FLEXIBILITY, {
        externalEventId: event.eventKey.externalEventId,
        flexibility: selectedFlex,
      });
    }
    if (this.props.onChangeFlex) {
      this.props.onChangeFlex(selectedFlex, externalEventId);
    }
  };

  private onChangeRoom = (allowSimilarRooms: boolean) => {
    const { event, enableTracking } = this.props;
    const { externalEventId } = event.eventKey;
    if (enableTracking) {
      track(TrackingEvents.ONBOARDING.ONBOARDING_AUTOPILOT_SET_ROOM_FLEXIBILITY, {
        allowSimilarRooms,
        externalEventId: event.eventKey.externalEventId,
      });
    }
    if (this.props.onChangeRoom) {
      this.props.onChangeRoom(allowSimilarRooms, externalEventId);
    }
  };

  private onChangeTime = (
    allowedStart: DayOffsetTimeSlot | null,
    allowedEnd: DayOffsetTimeSlot | null,
  ) => {
    const { event, enableTracking } = this.props;
    const { externalEventId } = event.eventKey;

    if (enableTracking) {
      track(TrackingEvents.ONBOARDING.ONBOARDING_AUTOPILOT_SET_TIME_FLEXIBILITY, {
        allowedStart,
        allowedEnd,
        externalEventId,
      });
    }

    if (this.props.onChangeTime) {
      this.props.onChangeTime(allowedStart, allowedEnd, externalEventId);
    }
  };

  private toggleExpand = () => {
    this.setState({ expanded: !this.state.expanded });
  };

  private onClickView = () => {
    this.props.setPricingDialogOpen(true);
    track(TrackingEvents.PAYWALLS.MOVE_RANGES_CLICK_VIEW_PLANS);
  };

  private isPlanRestricted = () => {
    const { event } = this.props;

    const featureStates = event && getOrganizerFeatureStatesFromEvent(event);
    const moveRangesEnabledFromFeatureGrid = featureStates
      ? featureStates.mixedFreemiumOrgUIState.moveRangesEnabled
      : true;

    const restricted = !moveRangesEnabledFromFeatureGrid;

    return !!restricted;
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  private renderAdvancedOptions = () => {
    const {
      action,
      autopilotEnabled,
      allowSimilarRooms,
      selectedFlex,
      org,
      event,
      allowedStart,
      allowedEnd,
    } = this.props;
    const { expanded } = this.state;

    if (action === "autopilot-simple") {
      return null;
    }

    const showRoom = eventHasRoom(event);

    return (
      <div>
        <div style={s.advancedOptions.container} onClick={() => this.toggleExpand()}>
          <Settings {...s.advancedOptions.settings} />
          <span>{expanded ? "Hide" : "Show"} advanced settings</span>
          {expanded ? (
            <ExpandLess style={s.advancedOptions.showLess} />
          ) : (
            <ExpandLess style={s.advancedOptions.showMore} />
          )}
        </div>
        <div style={expanded ? s.advancedOptions.expanded : s.advancedOptions.collapsed}>
          <AutopilotFlexibility
            disabled={!autopilotEnabled}
            org={org}
            event={event}
            showRoom={showRoom}
            onChangeFlex={this.onChangeFlex}
            onChangeRoom={this.onChangeRoom}
            onChangeTime={this.onChangeTime}
            selectedFlex={selectedFlex}
            allowSimilarRooms={allowSimilarRooms}
            isAutopilotEnabled={autopilotEnabled}
            planRestricted={this.isPlanRestricted()}
            onClickView={this.onClickView}
            allowedStart={allowedStart}
            allowedEnd={allowedEnd}
            attendeeMeetingHoursIntersect={true}
            isExtension={true}
            fixed={false}
            surface="OnboardingFlexOneOnOnes1on1Dash"
          />
        </div>
      </div>
    );
  };

  private renderEventSummaryTime = (autopilotEnabled?: boolean) => {
    const { org, event, selectedFlex } = this.props;

    if (event.recurringEventId || (event.recurrenceRules && event.recurrenceRules.interval)) {
      // if recurring event show recurrence
      return (
        <EventSummaryRecurrence
          org={org}
          event={event}
          selectedFlex={autopilotEnabled && selectedFlex !== EFlex.Pin ? selectedFlex : EFlex.Pin}
          color={autopilotEnabled && selectedFlex !== EFlex.Pin ? colors.green.standard : undefined}
        />
      );
    } else {
      // else show normal time
      const startTime = event.startTime && new ZonedMoment(event.startTime.millis);
      const endTime = event.endTime && new ZonedMoment(event.endTime.millis);
      return (
        <EventSummaryTime
          org={org}
          event={event}
          startTime={startTime}
          endTime={endTime}
          selectedFlex={autopilotEnabled && selectedFlex !== EFlex.Pin ? selectedFlex : undefined}
          unemphasized
        />
      );
    }
  };

  private maybeRenderEventSummaryExternalAttendees = (personMap: {
    [email: string]: ISchema.IPerson;
  }) => {
    const { action, event, org } = this.props;
    const externalAttendees = event.attendees.filter(
      (a) => org.domains.indexOf(getDomainFromCalendarId(a.urnValue)) === -1,
    );

    if (action === "autopilot-simple" || externalAttendees.length < 1) {
      return "";
    }

    return (
      <EventSummaryExternalAttendees
        org={org}
        attendees={externalAttendees}
        persons={Object.keys(personMap).map((p) => personMap[p])}
        color={colors.peach.darker}
      />
    );
  };

  private maybeRenderEventSummaryAttendees = (personMap: { [email: string]: ISchema.IPerson }) => {
    const { action, event, org } = this.props;
    const internalAttendees = event.attendees.filter(
      (a) => org.domains.indexOf(getDomainFromCalendarId(a.urnValue)) !== -1,
    );

    if (action === "autopilot-simple") {
      return null;
    }

    return (
      <EventSummaryAttendees
        org={org}
        attendees={internalAttendees}
        persons={Object.keys(personMap).map((p) => personMap[p])}
      />
    );
  };

  public render() {
    const t = this.context;
    const {
      event,
      action,
      personMap,
      autopilotEnabled,
      isExternalMeeting,
      isDisabled,
      attendeeNotInDomain,
      organizerNotClockwiseUser,
      isAllDayEvent,
    } = this.props;
    const { expanded } = this.state;

    const organizerPerson = personMap[event.organizer.urnValue];
    const organizerName =
      organizerPerson && organizerPerson.profile ? organizerPerson.profile.givenName : null;
    const organizerScheduled = !!organizerName ? `${organizerName} scheduled` : "Scheduled";

    const startAgoTime = new ZonedMoment(event.createdMillis);
    const startAgo = startAgoTime.getMoment().isValid() && startAgoTime.fromNow();

    const canAutopilot = !isDisabled;
    const warningText = getWarningText(
      t,
      maybeGetWarningType(
        event,
        canAutopilot,
        attendeeNotInDomain || false,
        organizerNotClockwiseUser || false,
        isAllDayEvent || false,
      ),
    );

    let getTooltipText: undefined | (() => string | JSX.Element);
    if (warningText) {
      getTooltipText = () => warningText;
    } else {
      getTooltipText = isDisabled
        ? undefined
        : () => t("autopilot-form:toggle-tooltip", { on: !!autopilotEnabled });
    }

    return (
      <div
        style={{
          ...s.container,
          borderColor: !autopilotEnabled
            ? greys.borderGrey
            : !isExternalMeeting
            ? helpers.opacity(colors.green.darker, 0.2)
            : helpers.opacity(colors.blueCool.darker, 0.2),
          height: expanded ? "auto" : "fit-content",
          minHeight: action === "autopilot-simple" ? 0 : s.container.minHeight,
        }}
      >
        <div style={s.meeting} key={event.eventKey.externalEventId}>
          {action === "invite" && startAgo ? (
            <div style={s.scheduledAgo}>
              {organizerScheduled} {startAgo}
            </div>
          ) : (
            ""
          )}
          <div style={s.header.container}>
            <span style={s.header.icon}>{!!autopilotEnabled ? `❇️` : `🗓`}</span>
            <span style={s.header.title}>{removeAutopilotEmoji(event.title)}</span>
            {action === "autopilot" || action === "autopilot-simple" ? (
              <TooltipToggle
                toggledOn={!!autopilotEnabled}
                onToggle={this.onAutopilotToggle}
                disabled={isDisabled}
                loading={false}
                toolTipKey={event.eventKey.externalEventId}
                getTooltipText={getTooltipText}
              />
            ) : (
              ""
            )}
            {action === "invite" ? (
              <A onClick={() => this.onClickInvite()}>Invite {organizerName}</A>
            ) : (
              ""
            )}
          </div>
          {this.maybeRenderEventSummaryAttendees(personMap)}
          {this.maybeRenderEventSummaryExternalAttendees(personMap)}
          {action !== "autopilot-simple" && <EventSummaryRoom attendees={event.attendees} />}
          {this.renderEventSummaryTime(autopilotEnabled)}
          {this.renderAdvancedOptions()}
        </div>
      </div>
    );
  }
}

//////////////////
// REDUX
//////////////////
function mapStateToProps(_state: IReduxState, _ownProps: IContainer) {
  return {};
}

const reduxContainer = connect(mapStateToProps, {
  setSettingsDialogOpen,
  setPricingDialogOpen,
  toggleExpanded,
})(OnboardingAutopilotEventCmpt);

//////////////////
// RELAY
//////////////////
export const OnboardingAutopilotEvent = createFragmentContainer<IContainer>(
  reduxContainer,
  fragments,
);
